import { rd } from '@passionware/monads';
import { AlertTriangle, Check, X } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from 'v5/design-system/Badge';
import { Skeleton } from 'v5/design-system/Skeleton';
import { useBookingTabStatus } from '../hooks/useBookingTabStatus';

interface VisitorManagementTabProps {
  bookingId: number;
}

/**
 * Hook that returns tab configuration for visitor management.
 * Includes status badge on the tab label based on vendor states.
 */
export const VisitorManagementTab = ({
  bookingId
}: VisitorManagementTabProps) => {
  const { t } = useTranslation('visitor-management');

  // Calculate status for tab badge
  const statusData = useBookingTabStatus(bookingId);

  const tabBadge = rd
    .journey(statusData)
    .wait(<Skeleton className="size-lh" />) // Don't show badge while loading
    .catch(() => null) // Don't show badge on error
    .map(({ overallStatus }) => {
      switch (overallStatus) {
        case 'all-synced':
          return (
            <Badge
              variant="success"
              tone="solid"
              size="sm"
              className="gap-1 text-xs"
            >
              <Check className="w-2 h-2" />
              <span>In sync</span>
            </Badge>
          );
        case 'has-drift':
          return (
            <Badge
              variant="attention"
              tone="solid"
              size="sm"
              className="gap-1 text-xs"
            >
              <AlertTriangle className="w-2 h-2" />
              <span>Drift</span>
            </Badge>
          );
        case 'has-errors':
          return (
            <Badge
              variant="danger"
              tone="solid"
              size="sm"
              className="gap-1 text-xs"
            >
              <X className="w-2 h-2" />
              <span>Error</span>
            </Badge>
          );
        case 'not-eligible':
          return (
            <Badge
              variant="gray"
              tone="solid"
              size="sm"
              className="gap-1 text-xs"
            >
              <span>Not eligible</span>
            </Badge>
          );
        case 'no-vendors':
        default:
          return (
            <Badge
              variant="gray"
              tone="subtle"
              size="sm"
              className="gap-1 text-xs"
            >
              <span>Never synced</span>
            </Badge>
          );
      }
    });

  return (
    <div className="contents tailwind-root">
      <div className="flex items-center gap-2">
        <span>{t('visitorManagement.bookingTab.title')}</span>
        {tabBadge}
      </div>
    </div>
  );
};
